import CirclePlus from 'assets/circle-plus.svg?react'
import { AxiosError } from 'axios'
import { LoadingBlur } from 'components/LoadingBlur'
import { AssetOverviewEditorGate } from 'features/assetDatabase'
import { AssetOverviewDataContext } from 'features/assetDatabase/contexts/AssetOverviewDataLayer'
import { createGeckoReportData } from 'features/assetDatabase/utils/createGeckoReportData'
import { useRolodexEdit } from 'features/fde'
import React, { useContext } from 'react'
import { styled } from 'styled-components'
import {
  GeckoComponent,
  GeckoFileCategory,
  GeckoInspection,
  GeckoUnit,
  RolodexEntryPatchJson,
  RolodexEntryType,
} from 'types'
import { v4 as uuidv4 } from 'uuid'

import { useImagesContext } from './ImagesContext'

const CirclePlusIcon = styled(CirclePlus)`
  width: 1rem;
  height: 1rem;
`

const IconContainer = styled.div`
  display: flex;
  width: var(--Height-control-height, 2.5rem);
  height: var(--Height-control-height, 2.5rem);
  justify-content: center;
  align-items: center;
`

export const UploadButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: var(--Height-control-height, 40px);
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.forecast.defaultGhostButton};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }

  &:active {
    background-color: ${({ theme }) => theme.forecast.activeGhostButton};
  }
`

const UploadButtonText = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: var(--Height-control-height, 40px);
  padding-right: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const ErrorMessageContainer = styled.div`
  height: var(--Height-control-height, 2.5rem);
  background-color: rgba(150, 0, 0, 0.25);
  padding: 0.75rem;
  margin: -0.5rem;
`

export const StyledUploadButton: React.FC<{
  isAssetThumbnail: boolean
  text?: string
  componentID?: string
  inspectionID?: string
}> = (p) => {
  const [isUploadingFile, setIsUploadingFile] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [clearErrorTimeout, setClearErrorTimeout] = React.useState<
    NodeJS.Timeout | undefined
  >(undefined)

  return (
    <UploadButton
      isAssetThumbnail={p.isAssetThumbnail}
      isUploadingFile={isUploadingFile}
      setIsUploadingFile={setIsUploadingFile}
      setErrorMessage={setErrorMessage}
      clearErrorTimeout={clearErrorTimeout}
      setClearErrorTimeout={setClearErrorTimeout}
      componentID={p.componentID}
      inspectionID={p.inspectionID}>
      {errorMessage.length > 0 ? (
        <ErrorMessageContainer>{errorMessage}</ErrorMessageContainer>
      ) : (
        <>
          <IconContainer>
            <CirclePlusIcon />
          </IconContainer>
          {p.text ? <UploadButtonText>{p.text}</UploadButtonText> : null}
          {isUploadingFile ? <LoadingBlur /> : null}
        </>
      )}
    </UploadButton>
  )
}

export const UploadButton: React.FC<
  {
    children: React.ReactNode
    isAssetThumbnail: boolean
    isUploadingFile?: boolean
    setIsUploadingFile?: React.Dispatch<React.SetStateAction<boolean>>
    setErrorMessage: React.Dispatch<React.SetStateAction<string>>
    clearErrorTimeout: NodeJS.Timeout | undefined
    setClearErrorTimeout: React.Dispatch<
      React.SetStateAction<NodeJS.Timeout | undefined>
    >
    componentID?: string
    inspectionID?: string
  } & React.HTMLAttributes<HTMLDivElement>
> = ({
  children,
  isAssetThumbnail,
  isUploadingFile,
  setIsUploadingFile,
  setErrorMessage,
  clearErrorTimeout,
  setClearErrorTimeout,
  componentID,
  inspectionID,
}) => {
  const fileInputRef = React.createRef<HTMLInputElement>()
  const { unit } = useContext(AssetOverviewDataContext)
  const { rolodexPatch, rolodexUploadImage, rolodexDelete } = useRolodexEdit()
  const { forceRefresh, components, inspections } = useImagesContext()

  const handleUpload = async () => {
    if (
      !unit ||
      !fileInputRef.current ||
      fileInputRef.current.files?.length === 0
    )
      return

    if (setIsUploadingFile) setIsUploadingFile(true)
    const files = Array.from(fileInputRef.current.files || [])
    fileInputRef.current.value = ''
    let componentIDToUse = componentID
    if (!componentID) {
      const inspection = inspections.find(
        (inspection) => inspection.id === inspectionID
      )
      componentIDToUse = inspection?.tags.component
    }
    const tagsToUse = tags(unit)

    if (!tagsToUse) {
      setErrorMessage('No tags found for unit')
      handleErrorTimeout()
      if (setIsUploadingFile) setIsUploadingFile(false)
      return
    }

    let successCount = 0
    let failCount = 0
    let lastErrorMessage = ''

    // Process each file sequentially
    for (const file of files) {
      const uuid = uuidv4()

      let patchResult
      let uploadResult
      let errorOccurred = false

      const patchBody = {
        id: uuid,
        type: RolodexEntryType.REPORT,
        data: getEntryData(
          unit,
          file,
          components,
          inspections,
          componentIDToUse,
          inspectionID
        ),
        tags: tagsToUse,
      }
      // Must patch rolodex before uploading file
      try {
        patchResult = await rolodexPatch([patchBody as RolodexEntryPatchJson])
      } catch (e) {
        failCount++
        errorOccurred = true
        if (e instanceof AxiosError && e.message) {
          lastErrorMessage = e.message
        } else {
          lastErrorMessage = 'Unknown patch error!'
        }
        continue // Skip to next file
      }

      if (patchResult && patchResult.status === 200) {
        try {
          uploadResult = await rolodexUploadImage(uuid, file, uuid)
          if (uploadResult && uploadResult.status === 200) {
            successCount++
          } else {
            errorOccurred = true
            failCount++
            lastErrorMessage = 'Upload failed with non-200 status'
          }
        } catch (e) {
          errorOccurred = true
          failCount++
          if (e instanceof AxiosError && e.message) {
            lastErrorMessage = e.message
          } else {
            lastErrorMessage = 'Unknown upload error!'
          }
        }
      }

      // If upload fails, delete the patch entry
      if (errorOccurred) {
        await rolodexDelete(patchBody.id)
      }
    }

    // Display appropriate message based on results
    if (failCount > 0) {
      if (successCount > 0) {
        setErrorMessage(
          `Uploaded ${successCount} files, failed to upload ${failCount} files. Last error: ${lastErrorMessage}`
        )
      } else {
        setErrorMessage(
          `Failed to upload ${failCount} files. Error: ${lastErrorMessage}`
        )
      }
      handleErrorTimeout()
    }

    await forceRefresh()
    if (setIsUploadingFile) setIsUploadingFile(false)
  }

  // Helper function to handle error timeout
  const handleErrorTimeout = () => {
    if (clearErrorTimeout) clearTimeout(clearErrorTimeout)
    setClearErrorTimeout(
      setTimeout(() => {
        setErrorMessage('')
        setClearErrorTimeout(undefined)
      }, 5000)
    )
  }

  return (
    <AssetOverviewEditorGate>
      <UploadButtonContainer
        data-testid={isAssetThumbnail ? null : 'ext-Files-upload'}
        onClick={() => {
          if (!isUploadingFile) {
            setErrorMessage('')
            fileInputRef.current?.click()
          }
        }}>
        <input
          onChange={handleUpload}
          multiple={true}
          ref={fileInputRef}
          type='file'
          accept={isAssetThumbnail ? '.png, .jpg, .jpeg' : ''}
          hidden
        />
        {children}
      </UploadButtonContainer>
    </AssetOverviewEditorGate>
  )
}

const getEntryData = (
  unit: GeckoUnit | undefined,
  file: File | undefined,
  components: readonly GeckoComponent[],
  inspections: readonly GeckoInspection[],
  componentID: string | undefined,
  inspectionID: string | undefined
) => {
  const isInspection = inspectionID !== undefined
  const isComponent = componentID !== undefined
  const isUnit = !isInspection && !isComponent

  if (isInspection) {
    const inspection = inspections.find(
      (inspection) => inspection.id === inspectionID
    )
    const component = components.find(
      (component) => component.id === componentID
    )
    if (!inspection || !file || !unit || !component) return
    return {
      ...createGeckoReportData({
        file,
        fileCategory: GeckoFileCategory.IMAGE,
        units: [unit.id],
        components: [component.id],
        inspections: [inspection.id],
      }),
    }
  }
  if (isComponent) {
    const component = components.find(
      (component) => component.id === componentID
    )
    if (!component || !file || !unit) return
    return {
      ...createGeckoReportData({
        file,
        fileCategory: GeckoFileCategory.IMAGE,
        units: [unit.id],
        components: [component.id],
        inspections: undefined,
      }),
    }
  }
  if (isUnit) {
    if (!unit || !file) return
    return {
      ...createGeckoReportData({
        file,
        fileCategory: GeckoFileCategory.IMAGE,
        units: [unit.id],
        components: undefined,
        inspections: undefined,
      }),
    }
  }
}

const tags = (unit: GeckoUnit | undefined) => {
  if (!unit) return
  return {
    unit: unit.id,
    ...unit.tags,
  }
}
